import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  DollarSign, CheckCircle2, Clock, XCircle, Briefcase, MapPin,
  Loader2, Trophy, ArrowRight, Zap
} from "lucide-react";

export default function MyBidsSection() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyBids();
  }, []);

  const fetchMyBids = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/professionals/my-bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBids(response.data.bids || []);
    } catch (error) {
      console.error("Failed to fetch bids:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "accepted":
        return (
          <Badge className="bg-green-100 text-green-700">
            <Trophy className="w-3 h-3 mr-1" />
            Accepted
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            Not Selected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#1E40AF]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#1E40AF]" />
            My Bids
          </div>
          <Badge variant="outline">{bids.length} total</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {bids.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">You haven&apos;t submitted any bids yet.</p>
            <p className="text-sm text-gray-400">
              Your assigned leads appear on your dashboard above.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {bids.slice(0, 5).map((bid) => (
              <div
                key={bid.id}
                className={`p-4 rounded-xl border transition-all ${
                  bid.status === "accepted"
                    ? "border-green-200 bg-green-50"
                    : bid.status === "rejected"
                    ? "border-gray-200 bg-gray-50"
                    : "border-slate-200 hover:border-[#1E40AF]/30"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {bid.job_title || "Job"}
                      </h4>
                      {bid.job_urgency === "emergency" && (
                        <Badge className="bg-red-100 text-red-700 text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          Emergency
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      {bid.job_location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {bid.job_location}
                        </span>
                      )}
                      <span>{new Date(bid.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xl font-bold text-[#1E40AF]">${bid.bid_amount}</div>
                    <div className="mt-1">{getStatusBadge(bid.status)}</div>
                  </div>
                </div>
                
                {bid.status === "accepted" && (
                  <div className="mt-3 p-3 bg-green-100 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      🎉 Congratulations! Your bid was accepted. The customer will contact you soon.
                    </p>
                  </div>
                )}
              </div>
            ))}
            
            {bids.length > 5 && (
              <Link
                to="/pro/bids"
                className="block text-center text-sm text-[#1E40AF] hover:underline py-2"
              >
                View all {bids.length} bids
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
